import type { OutputProfile } from '@/core/outputProfile';
import { api, apiRequest } from '@/core/http/apiClient';
import { download } from '@/core/http/files';
import { exportFile } from '@/core/http/helpers';
import type {
  AccountsBuilderScreen,
  DisclosureSectionView,
  GenerateOutputProfilePayload,
  OverrideDisclosurePayload,
  OverrideValidationIssuePayload,
  SaveDisclosureAnswersPayload,
  SaveDisclosureAnswersResponse,
  SaveOutputProfilePayload,
  SaveWorkingPaperEvidencePayload,
  SaveWorkingPaperPayload,
  StatutoryDatabaseScreen,
  ValidationCentreScreen,
  WorkingPaperDetail,
  WorkingPaperEvidenceRow,
  WorkingPaperSignOffPayload,
  WorkingPapersScreen,
} from '@/core/models/phase2.models';

const ifMatch = (eTag?: string | null) => (eTag ? { headers: { 'If-Match': eTag } } : {});

export const phase2Service = {
  // ---- Working papers ----

  getWorkingPapers: (accountingPeriodId: string) =>
    api.get<WorkingPapersScreen>(`WorkingPapers/getWorkingPapers/${accountingPeriodId}`),

  getWorkingPaper: (accountingPeriodId: string, scheduleType: number) =>
    api.get<WorkingPaperDetail>(`WorkingPapers/getWorkingPaper/${accountingPeriodId}/${scheduleType}`),

  saveWorkingPaper: (payload: SaveWorkingPaperPayload, eTag?: string | null) =>
    api.post<WorkingPaperDetail>('WorkingPapers/saveWorkingPaper', payload, ifMatch(eTag)),

  signOffWorkingPaper: (payload: WorkingPaperSignOffPayload) =>
    api.post<WorkingPaperDetail>('WorkingPapers/signOffWorkingPaper', payload),

  reviewWorkingPaper: (payload: WorkingPaperSignOffPayload) =>
    api.post<WorkingPaperDetail>('WorkingPapers/reviewWorkingPaper', payload),

  saveEvidence: (payload: SaveWorkingPaperEvidencePayload) =>
    api.post<WorkingPaperEvidenceRow>('WorkingPapers/saveEvidence', payload),

  deleteEvidence: (accountingPeriodId: string, id: string) =>
    api.post<boolean>(`WorkingPapers/deleteEvidence/${accountingPeriodId}/${id}`, {}),

  uploadEvidenceFile: (
    accountingPeriodId: string,
    scheduleType: number,
    file: File,
    title: string,
    detail: string | null,
    id: string | null,
    journalId: string | null = null,
  ) => {
    const form = new FormData();
    form.append('accountingPeriodId', accountingPeriodId);
    form.append('scheduleType', `${scheduleType}`);
    form.append('title', title);
    if (detail) {
      form.append('detail', detail);
    }
    if (id) {
      form.append('id', id);
    }
    if (journalId) {
      form.append('journalId', journalId);
    }
    form.append('file', file, file.name);

    return api.post<WorkingPaperEvidenceRow>('WorkingPapers/uploadEvidenceFile', form);
  },

  /** APT338 follow-up 2: the explanation and the evidence attached to one journal. */
  getJournalEvidence: (accountingPeriodId: string, journalId: string) =>
    api.get<WorkingPaperEvidenceRow[]>(`WorkingPapers/getJournalEvidence/${accountingPeriodId}/${journalId}`),

  downloadEvidenceFile: async (accountingPeriodId: string, id: string) => {
    const response = await apiRequest<Blob>('GET', `WorkingPapers/downloadEvidenceFile/${accountingPeriodId}/${id}`, {
      responseType: 'blob',
    });
    download(response);
    return true;
  },

  deleteEvidenceFile: (accountingPeriodId: string, id: string) =>
    api.post<boolean>(`WorkingPapers/deleteEvidenceFile/${accountingPeriodId}/${id}`, {}),

  // ---- Statutory database ----

  getStatutoryDatabase: (accountingPeriodId: string, search: string, state: number) => {
    const params: Record<string, string> = { state: `${state}` };
    if (search) {
      params['search'] = search;
    }
    return api.get<StatutoryDatabaseScreen>(`StatutoryDatabase/getStatutoryDatabase/${accountingPeriodId}`, { params });
  },

  overrideDisclosure: (payload: OverrideDisclosurePayload) =>
    api.post<StatutoryDatabaseScreen>('StatutoryDatabase/overrideDisclosure', payload),

  getDisclosureSection: (periodId: string, sectionCode: string) =>
    api.get<DisclosureSectionView>(
      `accounts-production/periods/${periodId}/disclosures/${encodeURIComponent(sectionCode)}`,
    ),

  saveDisclosureAnswers: (periodId: string, sectionCode: string, payload: SaveDisclosureAnswersPayload) =>
    api.put<SaveDisclosureAnswersResponse>(
      `accounts-production/periods/${periodId}/disclosures/${encodeURIComponent(sectionCode)}`,
      payload,
    ),

  // ---- Accounts builder and output profiles ----

  getOutputProfiles: (accountingPeriodId: string) =>
    api.get<AccountsBuilderScreen>(`AccountsBuilder/getOutputProfiles/${accountingPeriodId}`),

  saveOutputProfile: (payload: SaveOutputProfilePayload, eTag?: string | null) =>
    api.post<AccountsBuilderScreen>('AccountsBuilder/saveOutputProfile', payload, ifMatch(eTag)),

  generateAccounts: (payload: GenerateOutputProfilePayload) =>
    exportFile('AccountsBuilder/generateAccounts', payload, payload.isPrint),

  // ---- Validation centre ----

  /** `outputProfile` scopes the eligibility issues to Full, Abridged or Filleted accounts; omitted covers the period. */
  getValidationCentre: (accountingPeriodId: string, category: string, outputProfile?: OutputProfile | null) => {
    const path = `ValidationCentre/getValidationCentre/${accountingPeriodId}`;
    const params: Record<string, string> = {};
    if (category && category !== 'All') params.category = category;
    if (outputProfile) params.outputProfile = outputProfile;
    return Object.keys(params).length > 0
      ? api.get<ValidationCentreScreen>(path, { params })
      : api.get<ValidationCentreScreen>(path);
  },

  overrideIssue: (payload: OverrideValidationIssuePayload) =>
    api.post<ValidationCentreScreen>('ValidationCentre/overrideIssue', payload),
};
